import React, { useState, useRef, useEffect } from 'react';
import '../App.css';

function SearchableSelect({ options, value, onChange, placeholder = 'Select...', disabled = false, loading = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  const filteredOptions = options.filter(option => {
    const search = searchTerm.toLowerCase();
    const optionText = typeof option === 'string' ? option : (option.label || option.value || '');
    return optionText.toLowerCase().includes(search);
  });

  const selectedOption = options.find(opt => {
    const optValue = typeof opt === 'string' ? opt : opt.value;
    return optValue === value;
  });
  const displayValue = selectedOption 
    ? (typeof selectedOption === 'string' ? selectedOption : (selectedOption.label || selectedOption.value))
    : '';

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSelect = (option) => {
    const optValue = typeof option === 'string' ? option : option.value;
    onChange(optValue);
    setIsOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
          handleSelect(filteredOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (highlightedIndex >= 0 && dropdownRef.current) {
      const highlightedElement = dropdownRef.current.children[highlightedIndex];
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex]);

  return (
    <div className="searchable-select-container" ref={containerRef}>
      <div
        className={`searchable-select ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
      >
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            className="searchable-select-input"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setHighlightedIndex(-1);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <div className="searchable-select-display">
            {displayValue || <span style={{ color: 'var(--brand-text-light)' }}>{placeholder}</span>}
          </div>
        )}
        <span className="searchable-select-arrow">▼</span>
      </div>
      
      {isOpen && (
        <div className="searchable-select-dropdown" ref={dropdownRef}>
          {loading && options.length === 0 ? (
            <div className="searchable-select-option no-results">Projects loading...</div>
          ) : filteredOptions.length === 0 ? (
            <div className="searchable-select-option no-results">No results found</div>
          ) : (
            filteredOptions.map((option, index) => {
              const optValue = typeof option === 'string' ? option : option.value;
              const optLabel = typeof option === 'string' ? option : (option.label || option.value);
              const isSelected = optValue === value;
              const isHighlighted = index === highlightedIndex;
              
              return (
                <div
                  key={index}
                  className={`searchable-select-option ${isSelected ? 'selected' : ''} ${isHighlighted ? 'highlighted' : ''}`}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  {optLabel}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;


